import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Sparkles } from 'lucide-react';

const DISMISS_KEY = 'hoibuur_welkom_banner_dismissed';

/**
 * OnboardingBanner — vriendelijke uitlegregel voor nieuwe (uitgelogde) bezoekers.
 *
 * Verschijnt boven de feed op `/`. Dismissable via X-knop, onthoud in
 * localStorage. Wordt nooit getoond aan ingelogde gebruikers.
 */
const OnboardingBanner = ({ user, t }) => {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  if (user || dismissed) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  // Veilig vertalen: als parent geen `t` doorgeeft, val terug op NL strings.
  const tr = (key, fallback) => (typeof t === 'function' ? (t(key) || fallback) : fallback);

  return (
    <div
      className="mb-6 flex items-center gap-3 rounded-2xl bg-[#FFF9E6] border border-[#FFD400]/40 px-4 py-3 animate-fade-in-up"
      role="region"
      aria-label={tr('onboarding.banner_aria', 'Welkomstbericht voor nieuwe bezoekers')}
      data-testid="onboarding-banner"
    >
      <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-[#FFD400]/30 flex items-center justify-center">
        <Sparkles className="w-4 h-4 text-[#8a6c00]" aria-hidden="true" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-[#3d3000] leading-snug">
          <span className="font-semibold">{tr('onboarding.title', 'Nieuw hier?')}</span>{' '}
          {tr('onboarding.body', 'Lees wat Hoi Buur is en hoe je meedoet.')}
        </p>
      </div>
      <Link
        to="/welkom"
        className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-[#8a6c00] text-white text-xs font-semibold px-3 py-1.5 hover:bg-[#6e5700] transition-colors"
        data-testid="onboarding-banner-cta"
      >
        {tr('onboarding.cta', 'Lees meer')}
      </Link>
      <button
        onClick={handleDismiss}
        className="flex-shrink-0 p-1 rounded-lg text-[#8a6c00]/70 hover:text-[#8a6c00] hover:bg-white/50 transition-colors"
        aria-label={tr('onboarding.close_aria', 'Banner sluiten')}
        data-testid="onboarding-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default OnboardingBanner;
